import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Empty,
  Pagination,
  Skeleton,
  Space,
  Tabs,
  message,
} from 'antd';
import {
  CheckOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { FiBell } from 'react-icons/fi';
import { HiOutlineBuildingLibrary } from 'react-icons/hi2';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import { refetchAdminNotificationCount } from '../../hooks/useAdminNotificationCount';
import adminNotificationService, {
  NOTIFICATION_TYPE_BANK_MISMATCH,
} from '../../services/adminNotificationService';
import {
  NOTIFICATION_STATUS_TABS,
  countUnreadNotifications,
  formatNotificationTime,
  getEntitySegmentLabel,
  getNotificationNavigationPath,
} from './notification.helpers';

// ── Entity chip config ──────────────────────────────────────────────────────
const ENTITY_CHIP = {
  investors: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100', dot: 'bg-blue-500' },
  suppliers:  { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-100', dot: 'bg-amber-500' },
  partners:   { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-100', dot: 'bg-violet-500' },
};

const defaultChip = { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-100', dot: 'bg-gray-400' };

function EntityChip({ segment }) {
  const cfg = ENTITY_CHIP[segment] || defaultChip;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {getEntitySegmentLabel(segment)}
    </span>
  );
}

// ── Resolved status badge ───────────────────────────────────────────────────
const STATUS_BADGE = {
  resolved_approved: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100', label: 'Approved' },
  resolved_rejected: { bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-100',     label: 'Rejected' },
  dismissed:         { bg: 'bg-gray-50',    text: 'text-gray-500',    border: 'border-gray-100',    label: 'Dismissed' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

// ── Notification icon avatar ────────────────────────────────────────────────
function NotifIcon({ segment, isUnread }) {
  const cfg = ENTITY_CHIP[segment] || defaultChip;
  return (
    <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${isUnread ? cfg.bg : 'bg-gray-50'} border ${isUnread ? cfg.border : 'border-gray-100'} transition-colors`}>
      <HiOutlineBuildingLibrary size={18} className={isUnread ? cfg.text : 'text-gray-400'} />
    </div>
  );
}

// ── Single notification row ─────────────────────────────────────────────────
function NotificationRow({ item, canManage, onRowClick, isLoading }) {
  const isUnread = !item.is_read;
  const timeStr = formatNotificationTime(item.source_event_at || item.created_at);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => !isLoading && onRowClick(item)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !isLoading && onRowClick(item)}
      className={`
        group relative flex items-start gap-3.5 px-4 py-4 rounded-xl border transition-all duration-150 cursor-pointer
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40
        ${isUnread
          ? 'bg-white border-theme-border-secondary shadow-sm hover:shadow-md hover:border-primary/30'
          : 'bg-theme-bg-secondary/50 border-transparent hover:bg-theme-bg-secondary hover:border-theme-border-secondary'}
        ${isLoading ? 'opacity-60 pointer-events-none' : ''}
      `}
    >
      {/* Unread indicator stripe */}
      {isUnread && (
        <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-primary" />
      )}

      {/* Icon */}
      <NotifIcon segment={item.entity_segment} isUnread={isUnread} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
          <span className={`text-sm leading-snug ${isUnread ? 'font-semibold text-theme-text-primary' : 'font-medium text-theme-text-secondary'}`}>
            {item.title}
          </span>
          <EntityChip segment={item.entity_segment} />
          <StatusBadge status={item.status} />
          {isUnread && (
            <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" title="Unread" />
          )}
        </div>

        <p className="text-xs text-theme-text-secondary leading-relaxed line-clamp-2 mb-1.5">
          {item.message}
        </p>

        <div className="flex flex-wrap items-center gap-2 text-[11px] text-theme-text-secondary">
          <span>{timeStr}</span>
          {item.metadata?.user_name && (
            <>
              <span className="w-1 h-1 rounded-full bg-theme-text-secondary/40" />
              <span className="font-medium text-theme-text-primary/70">{item.metadata.user_name}</span>
            </>
          )}
          {item.metadata?.user_pan && (
            <>
              <span className="w-1 h-1 rounded-full bg-theme-text-secondary/40" />
              <span className="font-mono">{item.metadata.user_pan}</span>
            </>
          )}
          {item.metadata?.submitted_via && item.metadata.submitted_via !== '—' && (
            <>
              <span className="w-1 h-1 rounded-full bg-theme-text-secondary/40" />
              <span>{item.metadata.submitted_via}</span>
            </>
          )}
        </div>
      </div>

      {/* Chevron */}
      <div className={`flex-shrink-0 flex items-center self-center transition-transform duration-150 group-hover:translate-x-0.5 ${isUnread ? 'text-primary/60' : 'text-theme-text-secondary/40'}`}>
        <RightOutlined style={{ fontSize: 11 }} />
      </div>
    </div>
  );
}

// ── Skeleton row ────────────────────────────────────────────────────────────
function NotificationSkeleton() {
  return (
    <div className="flex items-start gap-3.5 px-4 py-4 rounded-xl border border-theme-border-secondary bg-white">
      <Skeleton.Avatar active shape="square" size={40} style={{ borderRadius: 12 }} />
      <div className="flex-1">
        <Skeleton active paragraph={{ rows: 2 }} title={{ width: '55%' }} />
      </div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export default function NotificationsPage() {
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(PERMISSIONS.ADMIN_NOTIFICATIONS_MANAGE);

  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(false);
  const [markAllLoading, setMarkAllLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total_records: 0 });
  const [error, setError] = useState('');

  const loadNotifications = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const data = await adminNotificationService.listNotifications({
        status: statusFilter,
        page,
        limit: pagination.limit,
      });
      setItems(data.notifications || []);
      setPagination(data.pagination || { page: 1, limit: 20, total_records: 0 });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, pagination.limit]);

  useEffect(() => {
    loadNotifications(1);
  }, [loadNotifications]);

  const unreadOnPage = countUnreadNotifications(items);
  const hasUnreadOpen = statusFilter === 'open' && unreadOnPage > 0;

  const handleMarkAllRead = async () => {
    setMarkAllLoading(true);
    try {
      const result = await adminNotificationService.markAllAsRead();
      message.success(
        result?.updated_count
          ? `${result.updated_count} notification(s) marked as read.`
          : 'All notifications marked as read.',
      );
      refetchAdminNotificationCount();
      await loadNotifications(pagination.page);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Failed to mark all as read.');
    } finally {
      setMarkAllLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    const path = getNotificationNavigationPath(notification);
    if (!path) {
      message.warning('No destination available for this notification.');
      return;
    }

    if (
      canManage
      && notification.notification_type === NOTIFICATION_TYPE_BANK_MISMATCH
      && !notification.is_read
    ) {
      setActionLoadingId(notification.uuid);
      try {
        await adminNotificationService.markAsRead(notification.uuid);
        refetchAdminNotificationCount();
        setItems((prev) =>
          prev.map((n) => (n.uuid === notification.uuid ? { ...n, is_read: true } : n)),
        );
      } catch (err) {
        message.error(err?.response?.data?.message || err?.message || 'Failed to mark as read.');
      } finally {
        setActionLoadingId(null);
      }
    }

    navigate(path);
  };

  const tabs = NOTIFICATION_STATUS_TABS.map((tab) => {
    const isOpen = tab.key === 'open';
    return {
      key: tab.key,
      label: (
        <span className="flex items-center gap-1.5">
          {tab.label}
          {isOpen && unreadOnPage > 0 && (
            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-semibold flex items-center justify-center leading-none">
              {unreadOnPage}
            </span>
          )}
        </span>
      ),
    };
  });

  return (
    <DashboardPageWrapper>
      <PageBreadcrumbs />

      <div className="p-4 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <FiBell size={18} className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-theme-text-primary leading-tight">Notifications</h1>
              <p className="text-xs text-theme-text-secondary mt-0.5">
                Bank name mismatch alerts pending your review
              </p>
            </div>
          </div>
          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              size="small"
              onClick={() => loadNotifications(pagination.page)}
              loading={loading}
            >
              Refresh
            </Button>
            {canManage ? (
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                loading={markAllLoading}
                disabled={!hasUnreadOpen}
                onClick={handleMarkAllRead}
              >
                Mark all as read
              </Button>
            ) : null}
          </Space>
        </div>

        {error ? <Alert type="error" message={error} showIcon closable onClose={() => setError('')} /> : null}

        {/* Tabs */}
        <Tabs
          activeKey={statusFilter}
          onChange={(key) => { setStatusFilter(key); }}
          items={tabs}
          size="small"
        />

        {/* Notification list */}
        <div className="space-y-2">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <NotificationSkeleton key={i} />
            ))
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                <FiBell size={24} className="text-gray-300" />
              </div>
              <p className="text-sm font-medium text-theme-text-secondary">No notifications</p>
              <p className="text-xs text-theme-text-secondary/60 mt-1">
                  {statusFilter === 'open' ? "You're all caught up!" : 'No notifications found for this filter.'}
              </p>
            </div>
          ) : (
            items.map((item) => (
              <NotificationRow
                key={item.uuid}
                item={item}
                canManage={canManage}
                onRowClick={handleNotificationClick}
                isLoading={actionLoadingId === item.uuid}
              />
            ))
          )}
        </div>

        {/* Pagination */}
        {!loading && pagination.total_records > pagination.limit ? (
          <div className="flex justify-end pt-2">
            <Pagination
              current={pagination.page}
              pageSize={pagination.limit}
              total={pagination.total_records}
              onChange={(page) => loadNotifications(page)}
              showSizeChanger={false}
              size="small"
            />
          </div>
        ) : null}
      </div>
    </DashboardPageWrapper>
  );
}
